import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Trophy, Clock, CheckCircle2, XCircle, BookOpen, School, Rocket, Flag, AlertTriangle } from "lucide-react";
import { renderHTML } from "@/lib/htmlUtils";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Question {
  id: string;
  text: string;
  options: { id: string; text: string; is_correct?: boolean }[];
  correctOptionId: string;
  explanation?: string;
  categoryName?: string;
}

interface TestResultsProps {
  questions: Question[];
  answers: Record<string, string>;
  categories: any[];
  grade: string;
  subject: string;
  categoryName?: string;
  onRetake: () => void;
  onContinuePractice: () => void;
}

const TestResults: React.FC<TestResultsProps> = ({
  questions,
  answers,
  categories,
  grade,
  subject,
  categoryName,
  onRetake,
  onContinuePractice
}) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [reportingQuestion, setReportingQuestion] = useState<{
    id: string;
    text: string;
    selectedOptionId: string;
    correctOptionId: string;
    options: { id: string; text: string }[];
  } | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [suggestedCorrectOption, setSuggestedCorrectOption] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Calculate score
  const score = questions.filter(q => answers[q.id] === q.correctOptionId).length;
  const totalQuestions = questions.length;
  const percentage = Math.round((score / totalQuestions) * 100);
  
  // Convert raw answers to the format needed for display
  const formattedAnswers = questions.map(question => {
    // Find the selected option by ID
    const selectedOptionId = answers[question.id];
    const selectedOption = question.options.find(opt => opt.id === selectedOptionId) || 
      { id: '', text: 'No answer selected' };
    
    // Find correct option, either by correctOptionId or is_correct flag
    const correctOption = question.options.find(opt => 
      opt.id === question.correctOptionId || opt.is_correct === true
    ) || { id: '', text: 'No correct answer found' };
    
    // Determine if the answer is correct
    const isCorrect = selectedOptionId === correctOption.id;
    
    return {
      questionId: question.id,
      questionText: question.text || '',
      selectedOption: {
        id: selectedOption.id,
        text: selectedOption.text || ''
      },
      correctOption: {
        id: correctOption.id,
        text: correctOption.text || ''
      },
      isCorrect,
      explanation: question.explanation || '',
      categoryName: question.categoryName || '',
      options: question.options
    };
  });
  
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };
  
  const getFeedback = () => {
    if (percentage >= 90) return "Excellent! You're a superstar!";
    if (percentage >= 75) return "Great job! You're doing really well!";
    if (percentage >= 60) return "Good work! Keep practicing!";
    if (percentage >= 40) return "You're making progress! Let's keep learning!";
    return "Keep practicing! You'll get better each time!";
  };

  // Safely render content that might be HTML or plain text
  const safeRenderContent = (content: any) => {
    if (!content) return { __html: '' };
    return renderHTML(content);
  };
  
  const handleReportQuestion = (question: any) => {
    setReportingQuestion({
      id: question.questionId,
      text: question.questionText,
      selectedOptionId: question.selectedOption.id,
      correctOptionId: question.correctOption.id,
      options: question.options
    });
    setSuggestedCorrectOption("");
    setReportReason("");
  };
  
  const submitQuestionReport = async () => {
    if (!reportingQuestion) return;
    
    setIsSubmittingReport(true);
    
    try {
      const response = await supabase.functions.invoke("report-question", {
        body: {
          questionId: reportingQuestion.id,
          questionText: reportingQuestion.text,
          userConcern: reportReason,
          suggestedCorrectOptionId: suggestedCorrectOption,
          grade,
          subject,
          userId: user?.id
        }
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      toast({
        title: "Report Submitted",
        description: "Thank you for helping improve our questions!",
        variant: "default"
      });
      
      // Close the modal
      setReportingQuestion(null);
    } catch (error) {
      console.error("Error submitting report:", error);
      toast({
        title: "Error",
        description: "Failed to submit report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="bg-white/90 backdrop-blur-md rounded-3xl border-4 border-indigo-200 overflow-hidden shadow-xl mb-8">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between mb-6">
              <div className="text-center md:text-left mb-6 md:mb-0">
                <h2 className="text-2xl md:text-3xl font-bold text-indigo-700 mb-2">
                  Your Test Results
                </h2>
                <p className="text-indigo-600">
                  {categoryName ? `${categoryName} - ` : ''}Grade {grade}
                </p>
              </div>
              
              <div className="flex space-x-4">
                <div className="text-center">
                  <div className="bg-indigo-100 rounded-xl p-3 mb-1">
                    <Trophy className="h-6 w-6 text-indigo-600 mx-auto" />
                  </div>
                  <p className="text-sm text-gray-500">Score</p>
                  <p className="font-bold text-indigo-700">{score}/{totalQuestions}</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-purple-100 rounded-xl p-3 mb-1">
                    <Clock className="h-6 w-6 text-purple-600 mx-auto" />
                  </div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="font-bold text-purple-700">{formatTime(0)}</p>
                </div>
              </div>
            </div>
            
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Overall Score</span>
                <span className="font-bold text-indigo-700">{percentage}%</span>
              </div>
              <Progress 
                value={percentage} 
                className="h-4 rounded-full" 
                indicatorClassName={`${
                  percentage >= 75 ? 'bg-green-500' :
                  percentage >= 60 ? 'bg-blue-500' :
                  percentage >= 40 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
              />
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl mb-6">
              <h3 className="font-bold text-indigo-700 mb-1">Feedback</h3>
              <p className="text-indigo-600">{getFeedback()}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <Button
                onClick={onContinuePractice}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-xl text-white"
              >
                <School className="mr-2 h-4 w-4" />
                Continue Practice
              </Button>
              
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 rounded-xl"
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Go to Dashboard
              </Button>
              
              <Button
                onClick={() => setShowAnswers(!showAnswers)}
                variant="outline"
                className="border-2 border-purple-200 text-purple-600 hover:bg-purple-50 rounded-xl"
              >
                <Rocket className="mr-2 h-4 w-4" />
                {showAnswers ? "Hide Answers" : "Show Answers"}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
      
      {showAnswers && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-xl font-bold text-indigo-700 mb-4">Review Your Answers</h3>
          
          <div className="space-y-4 mb-8">
            {formattedAnswers.map((answer, index) => (
              <motion.div
                key={answer.questionId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`border-l-4 ${
                  answer.isCorrect ? "border-l-green-400" : "border-l-red-400"
                } rounded-xl overflow-hidden`}>
                  <div className="p-4">
                    <div className="flex items-start mb-3">
                      <span className="bg-indigo-100 text-indigo-700 font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        {answer.categoryName && (
                          <div className="text-sm text-indigo-600 mb-1">{answer.categoryName}</div>
                        )}
                        <div 
                          className="text-gray-900" 
                          dangerouslySetInnerHTML={safeRenderContent(answer.questionText)} 
                        />
                      </div>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => handleReportQuestion(answer)}
                          >
                            <Flag className="h-4 w-4 mr-1" />
                            <span className="sr-only sm:not-sr-only sm:inline-block text-xs">Report</span>
                          </Button>
                        </DialogTrigger>
                        
                        <DialogContent className="max-w-md">
                          {reportingQuestion && (
                            <>
                              <DialogHeader>
                                <DialogTitle>Report Question</DialogTitle>
                                <DialogDescription>
                                  Please let us know what's wrong with this question.
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="p-3 bg-amber-50 rounded-md mb-3">
                                <div className="text-sm text-amber-800 font-medium flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                  <div dangerouslySetInnerHTML={safeRenderContent(reportingQuestion.text)} />
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="suggested-answer" className="text-sm font-medium mb-1.5">
                                    Suggest correct answer (optional)
                                  </Label>
                                  <RadioGroup 
                                    value={suggestedCorrectOption} 
                                    onValueChange={setSuggestedCorrectOption}
                                    className="space-y-2 mt-1"
                                  >
                                    {reportingQuestion.options.map((option) => (
                                      <div key={option.id} className="flex items-center space-x-2">
                                        <RadioGroupItem 
                                          value={option.id} 
                                          id={`report-option-${option.id}`} 
                                          className="cursor-pointer"
                                        />
                                        <Label 
                                          htmlFor={`report-option-${option.id}`} 
                                          className="text-sm leading-snug cursor-pointer"
                                        >
                                          <div dangerouslySetInnerHTML={safeRenderContent(option.text)} />
                                        </Label>
                                      </div>
                                    ))}
                                  </RadioGroup>
                                </div>
                                
                                <div>
                                  <Label htmlFor="report-reason" className="text-sm font-medium mb-1.5">
                                    Reason for reporting
                                  </Label>
                                  <Textarea 
                                    id="report-reason"
                                    placeholder="Please explain what's wrong with this question or answer..."
                                    value={reportReason}
                                    onChange={(e) => setReportReason(e.target.value)}
                                    className="min-h-[100px]"
                                  />
                                </div>
                              </div>
                              
                              <DialogFooter>
                                <Button 
                                  variant="outline" 
                                  onClick={() => setReportingQuestion(null)}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={submitQuestionReport} 
                                  disabled={!reportReason || isSubmittingReport}
                                >
                                  {isSubmittingReport ? "Submitting..." : "Submit Report"}
                                </Button>
                              </DialogFooter>
                            </>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>
                    
                    <div className={`mb-3 p-3 rounded-lg ${
                      answer.isCorrect ? "bg-green-50" : "bg-amber-50"
                    }`}>
                      <div className="flex items-start">
                        <div className={`flex-shrink-0 ${
                          answer.isCorrect ? "text-green-500" : "text-red-500"
                        }`}>
                          {answer.isCorrect ? (
                            <CheckCircle2 className="h-5 w-5 mt-0.5" />
                          ) : (
                            <XCircle className="h-5 w-5 mt-0.5" />
                          )}
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-700">Your answer:</p>
                          <div 
                            className="text-gray-600"
                            dangerouslySetInnerHTML={safeRenderContent(answer.selectedOption.text)}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {!answer.isCorrect && (
                      <div className="mb-3 p-3 rounded-lg bg-green-50">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 mt-0.5 text-green-500 flex-shrink-0" />
                          <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-gray-700">Correct answer:</p>
                            <div 
                              className="text-gray-600"
                              dangerouslySetInnerHTML={safeRenderContent(answer.correctOption.text)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {answer.explanation && (
                      <div className="p-3 rounded-lg bg-blue-50">
                        <p className="text-sm font-medium text-blue-700 mb-1">Explanation:</p>
                        <div 
                          className="text-blue-600 text-sm"
                          dangerouslySetInnerHTML={safeRenderContent(answer.explanation)}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TestResults;
